/**
 * SessionPage (/compte) — the profile/self view + session management surface. The 5-state DoD:
 *   • hors-ligne  → OfflineState (read from useUiStore; identity is still shown from cache when held)
 *   • chargement  → LoadingState while the auth store resolves the session bundle
 *   • erreur      → ErrorState when the session seam hard-failed (not the graceful 501 degrade)
 *   • sans-droit  → handled upstream by ProtectedRoute (this screen needs no feature capability)
 *   • vide        → ReauthPanel (unauthenticated shell) when the session ended in-tab
 *
 * Identity + effective caps come from the auth store (the canonical session holder, degrade-aware
 * via the @/auth/sessionProvider seam). User management is a separate capability-gated nav entry.
 */
import { ErrorState, LoadingState, OfflineState } from "@/ui";
import { useT, useUiStore } from "@/app";
import { useAuthStore } from "@/auth";
import { ApiError } from "@/api";
import { SessionIdentityCard } from "./components/SessionIdentityCard";
import { CapabilitiesPanel } from "./components/CapabilitiesPanel";
import { ActiveSessionPanel } from "./components/ActiveSessionPanel";
import { ReauthPanel } from "./components/ReauthPanel";
import { useSessionView, useRefreshSession } from "./useSession";

export function SessionPage() {
  const { t } = useT();
  const online = useUiStore((s) => s.online);
  const status = useAuthStore((s) => s.status);
  const error = useAuthStore((s) => s.error);
  const refresh = useRefreshSession();
  const session = useSessionView();

  // chargement — the store is mid-login / mid-resolve and we have nothing to show yet.
  if (status === "loading" && !session) {
    return <LoadingState label={t("auth.session.title")} />;
  }

  // vide / sans session — the session ended in-tab: show the unauthenticated shell (re-auth).
  if (status !== "authenticated" || !session) {
    if (status === "error" && error && !(error instanceof ApiError && error.isUnauthenticated)) {
      return (
        <ErrorState
          message={error.message}
          code={error.code}
          onRetry={() => {
            void refresh();
          }}
        />
      );
    }
    return <ReauthPanel />;
  }

  // hors-ligne — surfaced as a banner by the shell; here we degrade gracefully but keep showing the
  // cached identity (local-first). A full offline takeover only when we have NO cached identity.
  if (!online && !session.tokenPresent) {
    return (
      <OfflineState
        cached={false}
        onRetry={() => {
          void refresh();
        }}
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      <header style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-1)" }}>
        <h1 style={{ fontSize: "var(--nv-fs-h1)", margin: 0 }}>{t("auth.session.title")}</h1>
        <p style={{ margin: 0, color: "var(--nv-slate)" }}>{t("auth.session.subtitle")}</p>
      </header>

      {!online && (
        <OfflineState
          cached
          onRetry={() => {
            void refresh();
          }}
        />
      )}

      <div
        style={{
          display: "grid",
          gap: "var(--nv-s-5)",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          alignItems: "start",
        }}
      >
        <SessionIdentityCard session={session} />
        <ActiveSessionPanel session={session} />
      </div>

      <CapabilitiesPanel capabilities={session.capabilities} />
    </div>
  );
}
